import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import MuiTableCell from '@mui/material/TableCell';
import EDBox from '../../EDBox/EDBox';

export type EDStickyTableCellSelectedEvent = (
  col: number,
  row: number,
  shift: boolean,
  ctrl: boolean,
  dragging: boolean
) => void;

export type EDStickyTableCellSelectionClickEvent = (ref: EventTarget) => void;

interface IProps {
  col: number;
  row: number;
  selected?: boolean;
  today?: boolean;
  outside?: boolean;
  onCellSelected?: EDStickyTableCellSelectedEvent;
  onSelectionClick?: EDStickyTableCellSelectionClickEvent;
}

export const EDStickyTableCell: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { onSelectionClick, onCellSelected } = props;
  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (
        props.selected &&
        !event.ctrlKey &&
        !event.shiftKey &&
        onSelectionClick
      ) {
        onSelectionClick(event.target);
      } else if (onCellSelected) {
        onCellSelected(
          props.col,
          props.row,
          event.shiftKey,
          event.ctrlKey,
          false
        );
      }
      event.stopPropagation();
      event.preventDefault();
    },
    [onCellSelected, onSelectionClick, props.col, props.row, props.selected]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (event.buttons === 1 && onCellSelected) {
        onCellSelected(
          props.col,
          props.row,
          event.shiftKey,
          event.ctrlKey,
          true
        );
      }
      event.stopPropagation();
      event.preventDefault();
    },
    [onCellSelected, props.col, props.row]
  );

  const handleClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  }, []);

  return (
    <StyledTableCell
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
      className={
        props.selected
          ? 'selected'
          : props.outside
          ? 'outside'
          : props.today
          ? 'today'
          : undefined
      }
    >
      <StyledBox>{props.children}</StyledBox>
    </StyledTableCell>
  );
};

const StyledTableCell = styled(MuiTableCell, { name: 'StickyTableCell' })(
  ({ theme }) => ({
    width: 'var(--col-width)',
    boxSizing: 'border-box',
    padding: 0,
    border: 0,
    position: 'relative',
    background: `var(--cell-background, ${theme.palette.background.paper})`,
    '&.today': {
      background: `var(--cell-today-background, ${theme.customProperties.errorComponent.accordionError})`,
    },
    '&.outside': {
      background: `var(--cell-outside-background, ${theme.customProperties.Legend.gray.faded})`,
    },
    '&.selected': {
      background: `var(--cell-selected-background, ${theme.customProperties.Fade.Teal.lightEmphasis})`,
    },
  })
);

const StyledBox = styled(EDBox, { name: 'StickyTableCellContent' })(
  ({ theme }) => ({
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    padding: 'var(--cell-padding, 0)',
    overflow: 'var(--cell-overflow, hidden)' as React.CSSProperties['overflow'],
    borderRight: `1px solid ${theme.customProperties.DataGrid.gridBorderColor}`,
    borderBottom: `1px solid ${theme.customProperties.DataGrid.gridBorderColor}`,
    display: 'flex',
    flexDirection:
      'var(--cell-direction, column)' as React.CSSProperties['flexDirection'],
    justifyContent: 'var(--cell-alignment, left)',
    alignItems: 'var(--cell-verticalAlignment, top)',
    textAlign:
      'var(--cell-textAlignment, left)' as React.CSSProperties['textAlign'],
  })
);
